import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../hooks/useSocket';
import { buscarMensagens, enviarMensagem, enviarAudioApi, transferirConversa, resolverConversa, listarUsuarios } from '../services/api';
import api from '../services/api';
import { CAMPANHAS, STATUS, iniciais, avColor, hora } from '../services/utils';

export default function Chat({ conv, onResolvida }) {
  const { usuario }  = useAuth();
  const { on, entrarConversa, sairConversa } = useSocket();

  const [mensagens,    setMensagens]    = useState([]);
  const [texto,        setTexto]        = useState('');
  const [enviando,     setEnviando]     = useState(false);
  const [gravando,     setGravando]     = useState(false);
  const [atendentes,   setAtendentes]   = useState([]);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showCRM,      setShowCRM]      = useState(false);
  const [respondendo,  setRespondendo]  = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef        = useRef([]);
  const bottomRef        = useRef(null);
  const fileInputRef     = useRef(null);

  const camp = CAMPANHAS[conv?.campanha] || {};
  const st   = STATUS[conv?.status]     || {};
  const av   = avColor(conv?.contato_id || 0);

  const carregar = useCallback(async () => {
    if (!conv) return;
    try {
      const { data } = await buscarMensagens(conv.id);
      setMensagens(data.mensagens);
    } catch {}
  }, [conv]);

  useEffect(() => {
    setMensagens([]);
    setTexto('');
    setShowTransfer(false);
    setShowCRM(false);
    setRespondendo(null);
    if (!conv) return;
    carregar();
    entrarConversa(conv.id);
    return () => sairConversa(conv.id);
  }, [conv?.id]);

  useEffect(() => {
    const off = on('nova_mensagem', (msg) => {
      if (msg.conversa_id === conv?.id) {
        setMensagens(prev => {
          const semTemp = prev.filter(m => !String(m.id).startsWith('temp_'));
          return [...semTemp, msg];
        });
      }
    });
    return off;
  }, [on, conv?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [mensagens]);

  async function handleEnviar(e) {
    e.preventDefault();
    if (!texto.trim() || enviando) return;
    const textoEnviar = texto.trim();
    setTexto('');
    setRespondendo(null);
    const msgTemp = {
      id: 'temp_' + Date.now(),
      direcao: 'enviada',
      conteudo: textoEnviar,
      enviada_em: new Date(),
      remetente_nome: usuario.nome,
    };
    setMensagens(prev => [...prev, msgTemp]);
    try {
      await enviarMensagem(conv.id, textoEnviar);
    } catch {}
  }

  async function handleArquivo(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result;
      const ext    = file.name.split('.').pop().toLowerCase();
      const tipo   = ['jpg','jpeg','png','gif','webp'].includes(ext) ? 'image' :
                     ['pdf'].includes(ext) ? 'document' :
                     ['doc','docx'].includes(ext) ? 'document' : 'document';
      const msgTemp = {
        id: 'temp_' + Date.now(),
        direcao: 'enviada',
        conteudo: tipo === 'image' ? '🖼️ Imagem' : '📄 ' + file.name,
        enviada_em: new Date(),
        remetente_nome: usuario.nome,
      };
      setMensagens(prev => [...prev, msgTemp]);
      try {
        await api.post('/conversas/' + conv.id + '/arquivo', {
          base64,
          tipo,
          nome: file.name,
          mimetype: file.type,
        });
      } catch {}
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function iniciarGravacao() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = e => chunksRef.current.push(e.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/ogg; codecs=opus' });
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result;
          const msgTemp = {
            id: 'temp_' + Date.now(),
            direcao: 'enviada',
            conteudo: '[Áudio]',
            enviada_em: new Date(),
            remetente_nome: usuario.nome,
            isAudio: true,
          };
          setMensagens(prev => [...prev, msgTemp]);
          try { await enviarAudioApi(conv.id, base64); } catch {}
        };
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setGravando(true);
    } catch {
      alert('Permissão de microfone negada.');
    }
  }

  function pararGravacao() {
    mediaRecorderRef.current?.stop();
    setGravando(false);
    mediaRecorderRef.current = null;
  }

  async function handleTransferir(atendenteId) {
    await transferirConversa(conv.id, atendenteId);
    setShowTransfer(false);
  }

  async function handleResolver() {
    if (!window.confirm('Marcar como resolvida?')) return;
    await resolverConversa(conv.id);
    onResolvida(conv.id);
  }

  async function abrirTransfer() {
    setShowTransfer(true);
    try {
      const { data } = await listarUsuarios();
      setAtendentes(data.usuarios.filter(u =>
        u.perfil === 'atendente' && u.id !== usuario.id && u.numero === conv.numero
      ));
    } catch {}
  }

  async function excluirMensagem(msgId) {
    if (!window.confirm('Excluir esta mensagem?')) return;
    try {
      await api.delete('/conversas/' + conv.id + '/mensagens/' + msgId);
      setMensagens(prev => prev.filter(m => m.id !== msgId));
    } catch {}
  }

  if (!conv) {
    return (
      <div style={styles.empty}>
        <div style={styles.emptyIcon}>
          <i className="ti ti-messages" style={{ fontSize: 40, color: 'var(--green)' }} />
        </div>
        <h3 style={styles.emptyTitle}>Chat MJ</h3>
        <p style={styles.emptySub}>Selecione uma conversa para começar</p>
      </div>
    );
  }

  return (
    <div style={styles.chat} className="animate-fade">
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div className="avatar" style={{ width: 40, height: 40, fontSize: 13, background: av.bg, color: av.color }}>
            {iniciais(conv.contato_nome || conv.telefone)}
          </div>
          <div>
            <div style={styles.contactName}>{conv.contato_nome || conv.telefone}</div>
            <div style={styles.headerMeta}>
              {conv.campanha && (
                <span className="pill" style={{ background: camp.bg, color: camp.text, fontSize: 10 }}>
                  {camp.label}
                </span>
              )}
              {conv.atendente_nome && (
                <span style={styles.atendenteName}>
                  <i className="ti ti-user" style={{ fontSize: 10 }} /> {conv.atendente_nome}
                </span>
              )}
              <span className="pill" style={{ background: st.bg, color: st.text, fontSize: 10 }}>
                {st.label}
              </span>
            </div>
          </div>
        </div>
        <div style={styles.headerActions}>
          <button className="btn" onClick={abrirTransfer} style={{ fontSize: 12 }}>
            <i className="ti ti-arrows-transfer-up" /> Transferir
          </button>
          <button className="btn" onClick={() => setShowCRM(true)} style={{ fontSize: 12 }}>
            <i className="ti ti-address-book" /> CRM
          </button>
          <button className="btn btn-primary" onClick={handleResolver} style={{ fontSize: 12 }}>
            <i className="ti ti-check" /> Resolver
          </button>
        </div>
      </div>

      <div style={styles.autoTag}>
        {conv.numero === 'campanhas'
          ? <><i className="ti ti-robot" /> Roteado automaticamente → {camp.label || 'Campanhas'}</>
          : <><i className="ti ti-building-store" /> Atendimento comercial</>
        }
      </div>

      <div style={styles.messages}>
        {mensagens.map((m, i) => (
          <MsgBubble
            key={m.id || i}
            msg={m}
            meuNome={usuario.nome}
            onExcluir={() => excluirMensagem(m.id)}
            onResponder={() => setRespondendo(m)}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {gravando && (
        <div style={styles.recordingBar}>
          <span style={styles.recordingDot} />
          <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 500 }}>Gravando áudio... clique em parar quando terminar</span>
        </div>
      )}

      {respondendo && (
        <div style={styles.respondendoBar}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: 'var(--green)', fontWeight: 600, marginBottom: 2 }}>
              Respondendo a {respondendo.remetente_nome || 'mensagem'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {respondendo.conteudo}
            </div>
          </div>
          <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18 }} onClick={() => setRespondendo(null)}>
            <i className="ti ti-x" />
          </button>
        </div>
      )}

      <form style={styles.inputBar} onSubmit={handleEnviar}>
        <input ref={fileInputRef} type="file" style={{ display: 'none' }} accept="image/*,.pdf,.doc,.docx" onChange={handleArquivo} />
        <button type="button" className="btn" style={styles.iconBtn}>
          <i className="ti ti-mood-smile" style={{ fontSize: 18 }} />
        </button>
        <button type="button" className="btn" style={styles.iconBtn} onClick={() => fileInputRef.current?.click()} title="Enviar arquivo">
          <i className="ti ti-paperclip" style={{ fontSize: 18 }} />
        </button>
        <button
          type="button"
          className="btn"
          style={{ ...styles.iconBtn, color: gravando ? '#dc2626' : undefined }}
          onClick={gravando ? pararGravacao : iniciarGravacao}
          title={gravando ? 'Parar gravação' : 'Gravar áudio'}
        >
          <i className={`ti ${gravando ? 'ti-player-stop-filled' : 'ti-microphone'}`} style={{ fontSize: 18 }} />
        </button>
        <input
          value={texto}
          onChange={e => setTexto(e.target.value)}
          placeholder={gravando ? 'Gravando...' : respondendo ? 'Respondendo...' : 'Digite sua mensagem...'}
          style={styles.input}
          disabled={enviando || gravando}
        />
        <button type="submit" disabled={!texto.trim() || enviando || gravando} style={styles.sendBtn}>
          <i className="ti ti-send" style={{ fontSize: 18 }} />
        </button>
      </form>

      {showTransfer && (
        <div style={styles.modalBg} onClick={() => setShowTransfer(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHead}>
              <span style={{ fontWeight: 600 }}>Transferir para</span>
              <button style={styles.modalClose} onClick={() => setShowTransfer(false)}>
                <i className="ti ti-x" />
              </button>
            </div>
            {atendentes.length === 0
              ? <p style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)' }}>Nenhum atendente disponível.</p>
              : atendentes.map((a, i) => {
                  const aav = avColor(i);
                  return (
                    <div key={a.id} style={styles.agentRow} onClick={() => handleTransferir(a.id)}>
                      <div className="avatar" style={{ width: 34, height: 34, fontSize: 11, background: aav.bg, color: aav.color }}>
                        {iniciais(a.nome)}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>{a.nome}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.email}</div>
                      </div>
                      <span style={{ ...styles.onlineDot, marginLeft: 'auto' }} />
                    </div>
                  );
                })
            }
          </div>
        </div>
      )}

      {showCRM && (
        <ModalCRMConversa conv={conv} onClose={() => setShowCRM(false)} />
      )}
    </div>
  );
}

function MsgBubble({ msg, onExcluir, onResponder }) {
  const [showMenu, setShowMenu] = useState(false);
  const out = msg.direcao === 'enviada';
  const isAudio  = msg.media_type === 'audio' || msg.isAudio || msg.conteudo === '[Áudio]';
  const isImagem = msg.media_type === 'image';
  const isDoc    = msg.media_type === 'document';

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', alignItems: out ? 'flex-end' : 'flex-start', marginBottom: 4 }}
      onMouseEnter={() => setShowMenu(true)}
      onMouseLeave={() => setShowMenu(false)}
    >
      {out && msg.remetente_nome && (
        <span style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 2, paddingRight: 4 }}>
          {msg.remetente_nome}
        </span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexDirection: out ? 'row-reverse' : 'row' }}>
        <div style={{
          maxWidth: '70%',
          padding: '8px 12px',
          borderRadius: out ? '14px 4px 14px 14px' : '4px 14px 14px 14px',
          background: out ? 'var(--bg-msg-out)' : 'var(--bg-msg-in)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          fontSize: 13,
          lineHeight: 1.5,
          color: 'var(--text-primary)',
          border: out ? 'none' : '1px solid var(--border)',
        }}>
          {isAudio && msg.media_url ? (
            <div style={{ minWidth: 200 }}>
              <audio controls style={{ width: '100%', height: 36 }} src={msg.media_url} />
            </div>
          ) : isAudio ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
              <i className="ti ti-microphone" style={{ fontSize: 16, color: 'var(--green)' }} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Mensagem de voz</span>
            </div>
          ) : isImagem && msg.media_url ? (
            <img src={msg.media_url} alt="Imagem" style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, display: 'block', cursor: 'pointer' }} onClick={() => window.open(msg.media_url, '_blank')} />
          ) : isDoc && msg.media_url ? (
            <a href={msg.media_url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--green)', fontSize: 13 }}>
              <i className="ti ti-file" style={{ fontSize: 20 }} />
              <span>{msg.conteudo || 'Documento'}</span>
            </a>
          ) : (
            msg.conteudo
          )}
          <div style={{ fontSize: 10, color: 'var(--text-muted)', textAlign: 'right', marginTop: 3 }}>
            {hora(msg.enviada_em)}
            {out && <i className="ti ti-checks" style={{ marginLeft: 4, color: 'var(--green)' }} />}
          </div>
        </div>

        {showMenu && !String(msg.id).startsWith('temp_') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <button onClick={onResponder} title="Responder" style={styles.msgBtn}>
              <i className="ti ti-arrow-back-up" style={{ fontSize: 13 }} />
            </button>
            {out && (
              <button onClick={onExcluir} title="Excluir" style={{ ...styles.msgBtn, color: '#dc2626' }}>
                <i className="ti ti-trash" style={{ fontSize: 13 }} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ModalCRMConversa({ conv, onClose }) {
  const [form, setForm] = useState({
    nome:        conv.contato_nome || '',
    telefone:    conv.telefone     || '',
    campanha:    conv.campanha     || '',
    status:      'novo',
    observacoes: '',
    cpf:         '',
    email:       '',
  });
  const [salvando, setSalvando] = useState(false);
  const [salvo,    setSalvo]    = useState(false);
  const [erro,     setErro]     = useState('');

  const STATUS_LIST  = ['novo','qualificado','em_negociacao','contratado','perdido'];
  const STATUS_LABEL = { novo:'Novo', qualificado:'Qualificado', em_negociacao:'Em negociação', contratado:'Contratado', perdido:'Perdido' };

  async function salvar() {
    setSalvando(true); setErro('');
    try {
      await api.post('/crm/leads', { ...form, contato_id: conv.contato_id || 1 });
      setSalvo(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao criar lead.');
    }
    setSalvando(false);
  }

  return (
    <div style={styles.modalBg} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHead}>
          <span style={{ fontWeight: 600 }}>Criar Lead no CRM</span>
          <button style={styles.modalClose} onClick={onClose}><i className="ti ti-x" /></button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {salvo ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <i className="ti ti-circle-check" style={{ fontSize: 40, color: '#22c55e' }} />
              <div style={{ fontWeight: 600, marginTop: 8 }}>Lead criado com sucesso!</div>
            </div>
          ) : (
            <>
              {[['nome','Nome completo'],['cpf','CPF'],['email','Email'],['telefone','Telefone']].map(([k,l]) => (
                <div key={k}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>{l}</label>
                  <input style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', fontSize: 12, fontFamily: 'var(--font-sans)' }}
                    value={form[k]||''} onChange={e => setForm(p => ({...p,[k]:e.target.value}))} />
                </div>
              ))}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Status</label>
                <select style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', fontSize: 12 }}
                  value={form.status} onChange={e => setForm(p => ({...p,status:e.target.value}))}>
                  {STATUS_LIST.map(c => <option key={c} value={c}>{STATUS_LABEL[c]}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Observações</label>
                <textarea style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-app)', fontSize: 12, minHeight: 70, resize: 'vertical', fontFamily: 'var(--font-sans)' }}
                  value={form.observacoes} onChange={e => setForm(p => ({...p,observacoes:e.target.value}))} />
              </div>
              {erro && <div style={{ padding: '8px 12px', background: '#fef2f2', borderRadius: 8, fontSize: 12, color: '#dc2626' }}>{erro}</div>}
              <button style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', borderRadius: 9, border: 'none', background: 'var(--green)', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}
                onClick={salvar} disabled={salvando}>
                {salvando ? 'Criando...' : 'Criar lead no CRM'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  chat: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  empty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: 'var(--bg-chat)' },
  emptyIcon: { width: 72, height: 72, borderRadius: '50%', background: 'rgba(108,63,197,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  emptyTitle: { fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' },
  emptySub:   { fontSize: 13, color: 'var(--text-muted)' },
  header: { padding: '12px 16px', background: '#fff', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexShrink: 0 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  contactName: { fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' },
  headerMeta: { display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 },
  atendenteName: { fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 },
  headerActions: { display: 'flex', gap: 6, flexShrink: 0 },
  autoTag: { textAlign: 'center', fontSize: 11, color: '#065f46', background: '#ecfdf5', padding: '5px 0', borderBottom: '1px solid #a7f3d0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, flexShrink: 0 },
  messages: { flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 2, background: 'var(--bg-chat)' },
  recordingBar: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: '#fef2f2', borderTop: '1px solid #fca5a5' },
  recordingDot: { width: 10, height: 10, borderRadius: '50%', background: '#dc2626', animation: 'pulse 1s infinite' },
  respondendoBar: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', background: '#f0fdf4', borderTop: '1px solid #a7f3d0', flexShrink: 0 },
  inputBar: { padding: '10px 14px', background: '#fff', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  iconBtn: { padding: '7px 8px', flexShrink: 0, border: 'none', color: 'var(--text-muted)' },
  input: { flex: 1, padding: '10px 14px', borderRadius: 24, border: '1px solid var(--border)', background: 'var(--bg-input)', fontSize: 13, color: 'var(--text-primary)' },
  sendBtn: { width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'var(--green)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' },
  msgBtn: { width: 24, height: 24, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.06)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 },
  modalBg: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { background: '#fff', borderRadius: 16, width: 320, maxHeight: 520, overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalHead: { padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: '#fff' },
  modalClose: { border: 'none', background: 'none', fontSize: 18, color: 'var(--text-muted)', cursor: 'pointer' },
  agentRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)', transition: 'background 0.1s' },
  onlineDot: { width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 },
};